"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useClimateState } from "./use-climate-state";
import { CopyViewLink } from "./view-link";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CLIMATE_LOCATIONS,
  type ClimateLocation,
} from "@/lib/climate/evidence";
import {
  WEATHER_METRICS,
  type ClimateHistory,
  type WeatherMetric,
} from "@/lib/climate/history";
import {
  DataTable,
  SectionTitle,
  Segments,
  Sources,
  useChartAnimation,
} from "./ui";
import s from "./climate.module.css";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const formatMonth = (month: string) =>
  `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
type LoadState =
  | { slug: string; status: "ready"; data: ClimateHistory }
  | { slug: string; status: "loading" | "error" };

function HistoryCharts({
  data,
  place,
}: {
  data: ClimateHistory;
  place: ClimateLocation;
}) {
  const [metric, setMetric] = useClimateState<WeatherMetric>(
    "metric",
    "mean_temp_c",
    Object.keys(WEATHER_METRICS) as WeatherMetric[],
  );
  const [view, setView] = useClimateState("historyView", "timeline", [
    "timeline",
    "season",
  ]);
  const [selectedMonth, setSelectedMonth] = useClimateState("month", "none", [
    "none",
    ...data.weather.map((month) => month.month),
    ...data.heatmap.days.map((day) => day.date.slice(0, 7)),
  ]);
  const [heatmapMode, setHeatmapMode] = useClimateState("heatmap", "weather", [
    "weather",
    "scores",
  ]);
  const animate = useChartAnimation();
  const settings = WEATHER_METRICS[metric];
  const years = [
    ...new Set(data.weather.map((month) => month.month.slice(0, 4))),
  ];
  const timeline = data.weather.map((month) => ({
    label: formatMonth(month.month),
    value: month.values[metric],
  }));
  const season = MONTHS.map((label, index) =>
    Object.fromEntries([
      ["label", label],
      ...years.map((year) => [
        year,
        data.weather.find(
          (month) =>
            month.month === `${year}-${String(index + 1).padStart(2, "0")}`,
        )?.values[metric] ?? null,
      ]),
    ]),
  );
  const hasWeather = timeline.some((point) => point.value !== null);
  const available = data.heatmap.days.filter(
    (day) => day.source === "actual" && day.score !== null,
  );
  const heatMonths = [
    ...new Set(data.heatmap.days.map((day) => day.date.slice(0, 7))),
  ].map((month) => {
    const days = data.heatmap.days.filter((day) => day.date.startsWith(month));
    const actual = days.filter(
      (day) => day.source === "actual" && day.score !== null,
    );
    const calendarDays = new Date(
      Number(month.slice(0, 4)),
      Number(month.slice(5, 7)),
      0,
    ).getDate();
    const complete =
      days.length === calendarDays &&
      actual.length >= Math.ceil(calendarDays * 0.8);
    return {
      month,
      samples: actual.length,
      days: calendarDays,
      score: complete
        ? Math.round(
            (actual.reduce((sum, day) => sum + day.score!, 0) / actual.length) *
              10,
          )
        : null,
    };
  });
  const selected =
    heatMonths.find((month) => month.month === selectedMonth) ??
    heatMonths.at(-2);
  const weatherValues = data.weather.flatMap((month) =>
    month.values[metric] === null ? [] : [month.values[metric]!],
  );
  const weatherMin = Math.min(...weatherValues);
  const weatherMax = Math.max(...weatherValues);
  const selectedWeather =
    data.weather.find((month) => month.month === selectedMonth) ??
    data.weather.at(-2);
  const water = MONTHS.map((label, index) => {
    const row = data.water.find((point) => point.month === index + 1);
    return {
      label,
      value:
        row && row.sampleYears > 0 && Number.isFinite(row.temperature)
          ? row.temperature
          : null,
      years: row?.sampleYears ?? 0,
    };
  });
  const colours = ["#8b5e31", "#16808f", "#a64f35"];
  return (
    <>
      <p className={s.coverage}>
        <span className={s.dot} style={{ background: "#16808f" }} />
        Stored history window: {data.window.start} to {data.window.end}.
        Coverage varies by metric.
      </p>
      <div className={s.twoCol}>
        <article className={`${s.panel} ${s.panelWide}`}>
          <div className={s.chartHead}>
            <div>
              <h3>Watch the recent seasons change.</h3>
              <p className={s.small}>
                Monthly means of modelled past weather
                {data.elevation !== null && place.sport === "piste"
                  ? ` at the stored ${data.elevation.toLocaleString("en-GB")} m weather reference elevation`
                  : ""}
              </p>
            </div>
            <Segments
              label="History chart view"
              options={[
                { value: "timeline", label: "Timeline" },
                { value: "season", label: "Compare seasons" },
              ]}
              value={view}
              onChange={setView}
            />
          </div>
          <label className={`${s.field} mb-5`}>
            Weather measure
            <select
              value={metric}
              onChange={(event) =>
                setMetric(event.target.value as WeatherMetric)
              }
            >
              {(Object.keys(WEATHER_METRICS) as WeatherMetric[])
                .filter(
                  (key) =>
                    WEATHER_METRICS[key].sports === "both" ||
                    WEATHER_METRICS[key].sports === place.sport,
                )
                .map((key) => (
                  <option key={key} value={key}>
                    {WEATHER_METRICS[key].label} · {WEATHER_METRICS[key].unit}
                  </option>
                ))}
            </select>
          </label>
          {hasWeather ? (
            <>
              <div
                className={s.chart}
                role="img"
                aria-label={`${settings.label} monthly history in ${settings.unit}. Chart data available below.`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={view === "timeline" ? timeline : season}
                    margin={{ top: 12, right: 15, bottom: 12, left: -15 }}
                    accessibilityLayer
                  >
                    <CartesianGrid stroke="#dce3dc" vertical={false} />
                    <XAxis
                      dataKey="label"
                      fontSize={11}
                      tickLine={false}
                      minTickGap={30}
                    />
                    <YAxis fontSize={11} tickLine={false} width={55} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 7 }}
                      formatter={(value, name) => [
                        `${value} ${settings.unit}`,
                        name === "value" ? settings.label : name,
                      ]}
                    />
                    {view === "timeline" ? (
                      <Line
                        dataKey="value"
                        stroke="#16808f"
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                        connectNulls={false}
                        isAnimationActive={animate}
                      />
                    ) : (
                      years.map((year, index) => (
                        <Line
                          key={year}
                          dataKey={year}
                          stroke={colours[index % colours.length]}
                          strokeWidth={2.5}
                          dot={{ r: 3 }}
                          connectNulls={false}
                          isAnimationActive={animate}
                        />
                      ))
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {view === "season" && (
                <div className={s.legend}>
                  {years.map((year, index) => (
                    <span key={year}>
                      <i
                        className={s.dot}
                        style={{ background: colours[index % colours.length] }}
                      />
                      {year}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className={s.empty}>
              <p>
                No complete monthly {settings.label.toLowerCase()} series is
                available yet for this location.
              </p>
            </div>
          )}
          <p className={s.note}>
            At least 80% of days in a full calendar month must have this
            measure. Partial and missing months stay blank. Snow depth is
            modelled at a reference elevation, not a measured resort-wide depth.
            Daily snowfall is averaged in cm/day, not a monthly total. These two
            years cannot establish a climate trend.
          </p>
          <DataTable
            headers={[
              "Month",
              `${settings.label} (${settings.unit})`,
              "Days with data",
            ]}
            rows={data.weather.map((month) => [
              formatMonth(month.month),
              month.values[metric] ?? "Insufficient coverage",
              `${month.samples[metric]}/${month.days}`,
            ])}
          />
        </article>
        <article className={s.panel}>
          <h3>Your season, month by month.</h3>
          <Segments
            label="Season heatmap measure"
            value={heatmapMode}
            onChange={setHeatmapMode}
            options={[
              { value: "weather", label: "Weather history" },
              { value: "scores", label: "Conditions scores" },
            ]}
          />
          {heatmapMode === "weather" ? (
            <>
              <p className={`${s.small} mt-4`}>
                {settings.label} · {settings.unit} · darker tiles mean higher
                values, not better conditions
              </p>
              <div
                className={s.heatmap}
                role="group"
                aria-label="Monthly weather heatmap"
              >
                {data.weather.map((month) => {
                  const value = month.values[metric];
                  const ratio =
                    value === null || weatherMax === weatherMin
                      ? 0
                      : (value - weatherMin) / (weatherMax - weatherMin);
                  return (
                    <button
                      key={month.month}
                      type="button"
                      className={s.monthTile}
                      aria-pressed={selectedWeather?.month === month.month}
                      onClick={() => setSelectedMonth(month.month)}
                      style={
                        value === null
                          ? undefined
                          : {
                              background: `hsl(187 38% ${94 - ratio * 56}%)`,
                              color: ratio > 0.72 ? "white" : "#16233B",
                            }
                      }
                      aria-label={`${formatMonth(month.month)}: ${value === null ? "insufficient coverage" : `${value} ${settings.unit}`}`}
                    >
                      <span>{formatMonth(month.month)}</span>
                      <strong>{value === null ? "·" : value.toFixed(1)}</strong>
                    </button>
                  );
                })}
              </div>
              <p className={`${s.small} mt-5`} aria-live="polite">
                {selectedWeather
                  ? `${formatMonth(selectedWeather.month)}: ${selectedWeather.samples[metric]} of ${selectedWeather.days} days have this measure. ${selectedWeather.values[metric] === null ? "Insufficient coverage for a monthly mean." : `Mean ${selectedWeather.values[metric]} ${settings.unit}.`}`
                  : "No monthly weather history available."}
              </p>
              <p className={`${s.small} mt-3`}>
                Change the weather measure above to explore snowfall, snow
                depth, wind or swell where applicable. Colours are relative to
                this location and measure. Missing and partial months stay
                blank.
              </p>
            </>
          ) : (
            <>
              <p className={`${s.small} mt-4`}>
                Recent {place.sport === "piste" ? "piste" : "surf"} conditions ·
                PeakWave score / 100
              </p>
              <div
                className={s.heatmap}
                role="group"
                aria-label="Recent monthly conditions heatmap"
              >
                {heatMonths.map((month) => (
                  <button
                    key={month.month}
                    className={s.monthTile}
                    type="button"
                    aria-pressed={selected?.month === month.month}
                    onClick={() => setSelectedMonth(month.month)}
                    data-good={
                      month.score !== null &&
                      month.score >= 40 &&
                      month.score < 70
                    }
                    data-great={month.score !== null && month.score >= 70}
                    aria-label={`${formatMonth(month.month)}: ${month.score ?? "insufficient coverage"}; ${month.samples} days with scores`}
                  >
                    <span>{formatMonth(month.month)}</span>
                    <strong>{month.score ?? "·"}</strong>
                  </button>
                ))}
              </div>
              <p className={`${s.small} mt-5`} aria-live="polite">
                {selected
                  ? `${formatMonth(selected.month)}: ${selected.samples} of ${selected.days} days have usable scores. ${selected.score === null ? "Not enough coverage for a monthly average." : `Average ${selected.score}/100.`}`
                  : "No scored history available."}
              </p>
              <p className={`${s.small} mt-3`}>
                {available.length} scored days in the existing rolling-year
                heatmap. A dot means insufficient coverage, not poor conditions.
                Scores combine weather and sport factors; they are not a climate
                index.
              </p>
              <DataTable
                headers={["Month", "Score / 100", "Scored days"]}
                rows={heatMonths.map((month) => [
                  formatMonth(month.month),
                  month.score ?? "Insufficient coverage",
                  `${month.samples}/${month.days}`,
                ])}
              />
            </>
          )}
        </article>
        <article className={s.panel}>
          {place.sport === "surf" ? (
            <>
              <h3>The sea’s seasonal temperature.</h3>
              <p className={s.small}>
                Stored monthly water-temperature climatology · °C
              </p>
              {water.some((row) => row.value !== null) ? (
                <>
                  <div
                    className={s.chartSmall}
                    role="img"
                    aria-label="Monthly historical water temperature climatology. Full data below."
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={water}
                        margin={{ top: 12, right: 12, left: -20, bottom: 10 }}
                        accessibilityLayer
                      >
                        <CartesianGrid vertical={false} stroke="#dce3dc" />
                        <XAxis dataKey="label" fontSize={11} tickLine={false} />
                        <YAxis fontSize={11} tickLine={false} />
                        <Tooltip
                          formatter={(value) => [
                            `${value}°C`,
                            "Water temperature",
                          ]}
                        />
                        <Line
                          dataKey="value"
                          stroke="#16808f"
                          strokeWidth={2.5}
                          dot={{ r: 3 }}
                          connectNulls={false}
                          isAnimationActive={animate}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <DataTable
                    headers={["Month", "Water °C", "Sample years"]}
                    rows={water.map((row) => [
                      row.label,
                      row.value ?? "Missing",
                      row.years,
                    ])}
                  />
                </>
              ) : (
                <div className={s.empty}>
                  <p>
                    No stored water-temperature climatology is available for
                    this spot yet.
                  </p>
                </div>
              )}
              <p className={s.small}>
                This describes the typical seasonal cycle in the stored archive,
                not a year-by-year warming trend. Its sampling years are shown
                where available; no future local water temperature has been
                inferred.
              </p>
            </>
          ) : (
            <>
              <h3>A season is more than a snowfall total.</h3>
              <p className={s.small}>
                A warmer winter can still deliver a major snowstorm. What
                matters for skiing is how long snow persists, whether rain
                follows, and whether lower links remain usable.
              </p>
              <p className={`${s.small} mt-4`}>
                A defensible future season-length estimate needs a local snow
                model, slope elevations and an operating definition. The
                published regional elevation and risk charts above provide
                context; the recent history here shows what PeakWave actually
                holds.
              </p>
              <Sources ids={["ski", "mountains"]} />
            </>
          )}
        </article>
      </div>
    </>
  );
}

export function LocalOutlook() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  const [slug, setSlug] = useClimateState<string>(
    "location",
    CLIMATE_LOCATIONS[0].slug,
    CLIMATE_LOCATIONS.map((location) => location.slug),
  );
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<LoadState>({ slug, status: "loading" });
  const place = CLIMATE_LOCATIONS.find((location) => location.slug === slug)!;
  useEffect(() => {
    if (!visible) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    let active = true;
    fetch(`/api/climate/history?location=${encodeURIComponent(slug)}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("History unavailable");
        const data: ClimateHistory = await response.json();
        if (data.slug !== slug) throw new Error("Mismatched location");
        if (active) setState({ slug, status: "ready", data });
      })
      .catch(() => {
        if (active) setState({ slug, status: "error" });
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [slug, attempt, visible]);
  const visibleState =
    state.slug === slug ? state : { slug, status: "loading" };
  return (
    <section ref={sectionRef} className={s.section} id="your-location">
      <div className={s.wrap}>
        <SectionTitle
          number="04 / Bring it closer to home"
          title="The bigger picture. Your familiar place."
        >
          Explore regional implications beside real recent history. The
          projections above come from climate studies; the charts below come
          from PeakWave’s stored conditions archive.
        </SectionTitle>
        <div className={s.localControls}>
          <label className={s.field}>
            Choose your location
            <select
              value={slug}
              onChange={(event) => {
                setSlug(event.target.value);
                setVisible(true);
                setState({ slug: event.target.value, status: "loading" });
              }}
            >
              {CLIMATE_LOCATIONS.map((location) => (
                <option key={location.slug} value={location.slug}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <span className={s.small}>
            Four regional case studies · more places in the conditions directory
          </span>
          <CopyViewLink
            anchor="your-location"
            label="Share this location view"
          />
        </div>
        <div className={s.localContext}>
          <div className={s.eyebrow}>
            {place.region} · regional interpretation
          </div>
          <h3>What could this mean for {place.name.split(",")[0]}?</h3>
          <p>{place.outlook}</p>
          <Sources ids={[place.source]} />
          <Link href={`/conditions/${place.slug}`}>
            Open local conditions, season heatmap and evidence →
          </Link>
        </div>
        {visibleState.status === "ready" && "data" in visibleState ? (
          <HistoryCharts key={slug} data={visibleState.data} place={place} />
        ) : visibleState.status === "error" ? (
          <div className={s.empty} role="status">
            <p>Season history is temporarily unavailable.</p>
            <p className={s.small}>
              The published climate projections above remain available.
            </p>
            <button
              type="button"
              onClick={() => {
                setState({ slug, status: "loading" });
                setAttempt((value) => value + 1);
              }}
            >
              Try loading history again
            </button>
          </div>
        ) : (
          <div className={s.empty} role="status" aria-busy="true">
            <p>Loading stored season history…</p>
          </div>
        )}
        <p className={s.note}>
          Archive coverage is currently up to two years for these weather charts
          and one rolling year for the score heatmap. Four or five recent
          seasons would still be too short to isolate climate change from
          natural variability. We show available coverage rather than filling
          gaps with invented data.
        </p>
      </div>
    </section>
  );
}
